import BankAccountDocumentOwnership from '@/components/BankAccountDocumentOwnership/index.vue';
import BankAccountSelectHolder from '@/components/BankAccountSelectHolder/index.vue';
import documentsService from '@/services/documents.service';

export default {
    name: 'BankAccountForm',
    props: ['accountType', 'editable', 'uuidAccount', 'selectedBankAccount'],
    components: {
        BankAccountDocumentOwnership,
        BankAccountSelectHolder
    },

    data() {
        return {
            accountInterveners: [],
            accountPeriodicOperations: {}, // Periodic operations related to the account.
            bankAccount: {},
            bankAccountPeriodicOperations: {}, // Periodic operations related to the bank account.
            createdBankAccount: {},
            isEdit: true,
            isLinkingOperation: false, // to disable btns while linking operations.
            isLoading: false,
            originalBankAccount: {},
            selectedFile: null,
            showLinkPeriodicOperations: true
        };
    },

    watch: {
        createdBankAccount: function () {
            this.openLinkPeriodicOperationsModal();
        }
    },

    async mounted() {
        this.isLoading = true;

        if (this.selectedBankAccount !== 'new') {
            this.bankAccount = this.selectedBankAccount;
        } else {
            this.bankAccount = {
                iban_code: '',
                swift_code: '',
                holder_document_id: ''
            };
        }

        this.originalBankAccount = this.bankAccount;
        this.isEdit = this.selectedBankAccount === 'new' ? false : true;
        this.getPeriodicOperations();
        await this.getAccountInterveners();

        this.isLoading = false;
    },

    computed: {
        needSwift() {
            if (!this.bankAccount.iban_code) return false;

            const originOfIbanCode = this.bankAccount.iban_code
                ? this.bankAccount.iban_code.slice(0, 2).toUpperCase()
                : '';
            return originOfIbanCode != 'ES';
        }
    },

    methods: {
        /**
         * Get periodic operations related to the account and the bank account.
         */
        async getPeriodicOperations() {
            const config = {
                params: {
                    $filter:
                        '{"status":[{"ne":"CANCELLED"}],"key_operation_type":[{"ina": "(\'SOLICITUD_REEMBOLSO_PERIODICO\',\'SOLICITUD_APORTACION_PERIODICA\',\'SOLICITUD_SUSCRIPCION_PERIODICA\')"}]}'
                }
            };
            const res = await this.$accountService.getAccountOperations(config, this.uuidAccount);
            this.accountPeriodicOperations = res.data.related_operations; // gets the periodic operations of the account.

            this.bankAccountPeriodicOperations = this.accountPeriodicOperations;
        },

        goToOperation(uuid) {
            this.$bvModal.hide('link-periodic-operations');
            this.$router.push(`/account/operation/${uuid}`);
        },

        checkHasPeriodicOperations() {
            if (
                this.bankAccountPeriodicOperations &&
                this.bankAccountPeriodicOperations.length > 0
            ) {
                this.$bvModal.show('periodic-operations');
                return;
            } else {
                this.save();
            }
        },
        cancelLinkingPeriodicOperations() {
            this.showLinkPeriodicOperations = false;
            this.$bvModal.hide('link-periodic-operations');
            this.$emit('update');
        },

        async getAccountInterveners() {
            try {
                const response = await this.$accountService.getAccountInterveners(
                    {},
                    this.uuidAccount
                );
                this.accountInterveners = response.data.accounts_intervener;
            } catch (err) {
                console.error(err);
                this.$toast.error(`Error: ${err}`);
            }
        },

        save() {
            this.isLoading = true;
            this.$accountService
                .updateAccountBankAccounts(
                    {},
                    this.bankAccount,
                    this.uuidAccount,
                    this.bankAccount.uuid
                )
                .then(res => {
                    this.isLoading = false;
                    this.uploadDocument(res.data.bank_account);
                    this.$emit('update');
                    this.$store.dispatch('resetDocumentItems');
                })
                .catch(err => {
                    this.isLoading = false;

                    this.$bvToast.toast(`${err.message}`, {
                        title: 'Error',
                        toaster: 'b-toaster-top-center',
                        autoHideDelay: 10000,
                        variant: 'danger'
                    });

                    console.error(err);
                });
        },

        openLinkPeriodicOperationsModal() {
            if (
                this.accountPeriodicOperations &&
                this.accountPeriodicOperations.length > 0 &&
                this.showLinkPeriodicOperations
            ) {
                this.$bvModal.show('link-periodic-operations');
                return;
            }
        },

        create() {
            this.isLoading = true;

            const bankAccountBody = {
                holder_document_id: this.bankAccount.holder
                    ? this.bankAccount.holder.document_id
                    : null,
                iban_code: this.bankAccount.iban_code,
                swift_code: this.bankAccount.swift_code
            };

            this.$accountService
                .insertAccountBankAccount({}, bankAccountBody, this.uuidAccount)
                .then(response => {
                    this.isLoading = false;

                    this.uploadDocument(response.data.bank_account);

                    this.createdBankAccount = { ...response.data.bank_account };

                    // if there are not periodic operations, refresh the page
                    if (
                        !this.accountPeriodicOperations ||
                        this.accountPeriodicOperations.length === 0
                    ) {
                        this.$store.dispatch('resetDocumentItems');
                        this.$emit('update');
                    }
                })
                .catch(err => {
                    this.isLoading = false;

                    this.$bvToast.toast(`${err.message}`, {
                        title: 'Error',
                        toaster: 'b-toaster-top-center',
                        autoHideDelay: 10000,
                        variant: 'danger'
                    });

                    console.error(err);
                });
        },

        async uploadDocument(bankAccount) {
            try {
                if (!this.selectedFile && this.isNeedBankAccountDocument) {
                    throw new Error('No file selected for upload.');
                }

                // Ahora, para las cuentas españolas, no se sube documento, se validan en el back
                // con YOiD
                if (!this.selectedFile && !this.isNeedBankAccountDocument) {
                    return null;
                }

                this.isLoading = true;

                const payload = {
                    section: 'BANK_ACCOUNTS',
                    files: [this.selectedFile],
                    filesName: [this.selectedFile.name],
                    category: 'DOCUMENTATION',
                    subcategory: 'DOCUMENTO_ACREDITATIVO_TITULARIDAD_CC',
                    validated: false,
                    uuid: this.uuidAccount,
                    uuid_bank_account: bankAccount.uuid
                };

                if (this.uuidAccount) {
                    payload.uuid_account = this.uuidAccount;
                }

                await documentsService.upload(payload);

                this.isLoading = false;
            } catch (err) {
                this.$bvToast.toast(`${err.message}`, {
                    title: `Error ${err.name}`,
                    autoHideDelay: 10000,
                    variant: 'danger'
                });
            }
        },

        cancel() {
            this.$emit('cancel');
        },

        isValidBankAccount(invalid) {
            if (invalid) return false;

            if (this.isNeedBankAccountDocument) {
                const isValid =
                    this.selectedFile &&
                    !this.showInvalidDocumentSize &&
                    !this.showInvalidDocumentFormat;

                return isValid;
            }

            const isHolder = Boolean(this.bankAccount.holder);

            if (!isHolder) return false;

            return true;
        },

        onIbanChange() {
            this.selectedFile = null;
        },

        onFileSelected(file) {
            this.selectedFile = file;
        },

        onSelectedHolder(holder) {
            this.bankAccount.holder = holder;
        }
    }
};
